import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  User, Users, Plane,
  Zap, 
  Bell, 
  Search, 
  ChevronDown,
  FileText,
  BarChart2,
  GraduationCap
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';

const Header = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(UserContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNominationsDropdownOpen, setIsNominationsDropdownOpen] = useState(false);
  const nominationsDropdownRef = useRef(null);

  const handleLogout = () => {
    if (window.confirm("Czy na pewno chcesz się wylogować?")) {
      logout();
      navigate('/login', { replace: true });
    }
  };

  const handleClickOutside = (event) => {
    if (nominationsDropdownRef.current && !nominationsDropdownRef.current.contains(event.target)) {
      setIsNominationsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = [
    { 
      icon: User, 
      text: 'Dashboard', 
      path: ['admin', 'adminves'].includes(user?.role) ? '/admin' : '/user'
    },
    ...(user?.role === 'workpermit_admin' ? [{
      icon: BarChart2,
      text: 'Work Permit Admin',
      path: '/workpermit/admin'
    }] : []),
    { 
      icon: Zap, 
      text: 'Work Permits', 
      path: user?.role === 'adminves' ? '/workpermit/admin' : '/workpermits' 
    },
    { 
      icon: FileText, 
      text: 'Nominations', 
      isDropdown: true,
      dropdownItems: [
        { text: 'Create', path: '/nominations/create' },
        { text: 'Nominations List', path: '/nominations/list' }
      ]
    },
    { 
      icon: Users, 
      text: 'Users', 
      path: '/users' 
    },
    { 
      icon: GraduationCap, 
      text: 'Trainings', 
      path: '/trainings' 
    },
    { 
      icon: Plane, 
      text: 'Travels', 
      path: '/travel' 
    },
  ];

  return (
    <header className="bg-gray-900 shadow-md">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <div className="flex items-center">
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold text-white">{user?.company_name || 'Użytkownik'}</h1>
          </div>
        </div>

        <div className="relative mr-4 hidden md:block">
          <input
            type="text"
            placeholder="Search..."
            className="w-64 px-4 py-2 text-gray-700 bg-gray-800 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button className="absolute right-0 top-0 mt-2 mr-2 text-gray-500 hover:text-white">
            <Search size={20} />
          </button>
        </div>

        <div className="flex items-center space-x-4">
          <button className="text-gray-400 hover:text-white transition duration-150">
            <Bell size={24} />
          </button>
          <div className="relative">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex items-center focus:outline-none"
            >
              {user?.profilePicture ? (
                <img
                  className="w-10 h-10 rounded-full border-2 border-blue-500 object-cover"
                  src={user.profilePicture}
                  alt={`${user.username}'s avatar`}
                />
              ) : (
                <div className="w-10 h-10 rounded-full border-2 border-blue-500 bg-gray-700 flex items-center justify-center">
                  <User size={20} className="text-gray-400" />
                </div>
              )}
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md overflow-hidden shadow-lg z-10">
                <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Profile
                </Link>
                <Link to="/settings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <nav className="bg-gray-800">
        <div className="container mx-auto px-4">
          <ul className="flex space-x-6 py-4 text-gray-400">
            {navItems.map((item, index) => (
              <li
                key={index}
                className="relative group hover:text-white transition duration-200"
              >
                <div className="flex items-center">
                  <item.icon className="mr-2" size={20} />
                  {item.isDropdown ? (
                    <>
                      <button
                        onClick={() => {
                          if (item.text === 'Nominations') {
                            setIsNominationsDropdownOpen(!isNominationsDropdownOpen);
                          }
                        }}
                        className="flex items-center focus:outline-none"
                      >
                        <span>{item.text}</span>
                        <ChevronDown className="ml-1" size={18} />
                      </button>

                      {item.text === 'Nominations' && isNominationsDropdownOpen && (
                        <div 
                          ref={nominationsDropdownRef} 
                          className="absolute left-0 mt-2 w-48 bg-gray-700 rounded-md shadow-lg z-10"
                        >
                          {item.dropdownItems.map((dropdownItem, idx) => (
                            <Link
                              key={idx}
                              to={dropdownItem.path}
                              className="block px-4 py-2 text-sm text-white hover:bg-gray-600"
                            >
                              {dropdownItem.text}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link to={item.path}>{item.text}</Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Header;